/**
 * FunnelContext — loads funnelMode from the backend once on startup.
 * 'strict': gates all content behind paywall (existing RequireAccess behaviour)
 * 'soft'  : lets user browse freely; paywall only shown when hitting Play
 *
 * Кешируем режим в localStorage, чтобы повторные заходы были мгновенными.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../services/api';

const LS_KEY = 'lf_funnel_mode';

function readCached() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'strict' || v === 'soft') return v;
  } catch {}
  return null;
}

function writeCached(mode) {
  try { localStorage.setItem(LS_KEY, mode); } catch {}
}

const FunnelContext = createContext({ funnelMode: 'strict', loading: false });

export const useFunnel = () => useContext(FunnelContext);

export const FunnelProvider = ({ children }) => {
  const cached = readCached();
  // Если кеш есть — loading сразу false, UI не ждёт ни миллисекунды
  const [funnelMode, setFunnelMode] = useState(cached || 'strict');
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    const finish = (nextMode) => {
      if (settled || cancelled) return;
      settled = true;
      if (nextMode) {
        setFunnelMode(nextMode);
        writeCached(nextMode);
      }
      setLoading(false);
    };

    // Если кеш был — обновляем тихо в фоне (без таймаута/блокировки)
    const timer = cached ? null : setTimeout(() => finish(), 2500);

    apiService
      .getSiteSettings()
      .then((data) => {
        const mode = data?.funnelMode;
        if (mode === 'strict' || mode === 'soft') finish(mode);
        else finish();
      })
      .catch(() => { if (!cached) finish(); });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FunnelContext.Provider value={{ funnelMode, loading }}>
      {children}
    </FunnelContext.Provider>
  );
};

export default FunnelContext;
